import Ajax from './Ajax';
import ReservationStore from '../stores/ReservationStore';
import PaymentStore from '../stores/PaymentStore';
import PaymentActions from '../actions/PaymentActions';
import ReservationActions from '@/actions/ReservationActions';

import { PAYMENT_STATUS, PAYMENT_CREDIT_CARD_ERROR_CODES } from 'arbitrip-common/general/constants';
import {
	classifyPaymentError,
	isPaymentMethodAcceptedStatus,
} from 'arbitrip-common/client/analytics/services/paymentInfoService';
import i18n from 'arbitrip-common/translation/desktop.js';

const MILLISECONDS = 1;
const SECONDS = 1000 * MILLISECONDS;
const pollTimeout = 2.6 * SECONDS;

var poller = null;
var status = 'stopped';
var couponValidation = null;
var paymentCallbacks = {};
const CALLBACK_PAYMENT_METHOD_ACCEPTED = 'onPaymentMethodAccepted';
const CALLBACK_PAYMENT_DECLINED = 'onPaymentDeclined';
const notifiedPaymentIds = {
	[CALLBACK_PAYMENT_METHOD_ACCEPTED]: new Set(),
	[CALLBACK_PAYMENT_DECLINED]: new Set(),
};

// Callbacks only report analytics, so they fire once per payment id and can never break the polling flow.
function notifyOncePerPayment(callbackName, paymentId, payload) {
	const callback = paymentCallbacks[callbackName];
	if (typeof callback !== 'function' || !paymentId || notifiedPaymentIds[callbackName].has(paymentId)) {
		return;
	}
	notifiedPaymentIds[callbackName].add(paymentId);
	try {
		callback(payload);
	} catch (e) {
		console.error('Poller: payment callback failed', callbackName, e);
	}
}

function isReservationValid(reservationStatus) {
    return ['pending-support', 'pending-supplier', 'approved', 'quote'].includes(reservationStatus);
}

export default {
	startPaymentStatusPolling: function (coupon_validation, callbacks) {
		var _self = this;
		status = 'running';
		couponValidation = coupon_validation;
		paymentCallbacks = callbacks || {};
		if (poller) return;
		(function poll() {
			if (status === 'stopped') {
				return;
			}
			console.log('poll run - sending http req..');
			const payment = PaymentStore.getPaymentData();
			Ajax.samplePaymentStatus(ReservationStore.getReservationData(), payment)
				.done(function (res) {
					const {
						paymentStatus: status,
						error_status_code,
						payment_url_ttl,
						threeds_expedia_additional_information,
						supplier_ref,
                        reservation_status,
						allow_retry,
						bnpl_usd_amount_with_fee,
						card_brand,
					} = res;
					const paymentId = payment && payment.id;
					if (isPaymentMethodAcceptedStatus(status)) {
						notifyOncePerPayment(CALLBACK_PAYMENT_METHOD_ACCEPTED, paymentId, { cardBrand: card_brand });
					}
					// Once the server has persisted the BNPL charge amount (post coupon + points), replace the
					// client's gross init-time placeholder so the confirmation page shows the authoritative value.
					if (typeof bnpl_usd_amount_with_fee === 'number') {
						ReservationActions.updateBnplAmount(bnpl_usd_amount_with_fee);
					}
					if (payment_url_ttl) {
						const now = new Date();
						if (now > payment_url_ttl) {
							// TODO! Redirect to initPayment to generate another URL
							console.log('url ttl has expired');
						}
					}
					// TODO: if not what we're looking for - setTimeout(doPoll, 5000);
					// console.log("doPoll - done : ", res);
					console.log('url link is valid till: ', payment_url_ttl);
					switch (status) {
                        case PAYMENT_STATUS.NA: {
                            // TODO: (not very good)
                            PaymentActions.markPaymentAsFailed('not_exists');
                            break;
                        }
                        case PAYMENT_STATUS.J5:
                            if (isReservationValid(reservation_status)) {
                                PaymentActions.markPaymentAsCharged(couponValidation);
                            } else if (reservation_status.includes('cancel') || reservation_status.includes('error')) {
                                PaymentActions.markPaymentAsFailed('error');
                            } else {
                                console.log('poll - no changes for reservation, retrying..');
                                return (poller = setTimeout(poll, pollTimeout));
                            }
                            break;
                        case PAYMENT_STATUS.BILLED_IN_PROCESS:
                        case PAYMENT_STATUS.TOKENIZED_WITHOUT_BILLING:
                        case PAYMENT_STATUS.INITIALIZED: {
                            console.log('poll - no changes, retrying..');
                            return (poller = setTimeout(poll, pollTimeout));
                        }
                        case PAYMENT_STATUS.TOKENIZED_WITHOUT_BILLING_WITH_PAYMENT_SESSION: {
                            PaymentActions.save3DSInformation(threeds_expedia_additional_information);
                            break;
                        }
                        case PAYMENT_STATUS.BNPL_PENDING:
                        case PAYMENT_STATUS.CHARGE_LATER: {
                            // TODO! Take from constants
                            PaymentActions.markPaymentAsCharged(couponValidation);
                            break;
                        }
                        case PAYMENT_STATUS.POSTPONED: {
                            PaymentActions.markPaymentAsCharged(couponValidation);
                            break;
                        }
                        case PAYMENT_STATUS.PENDING_MANUAL_BILLING: {
                            PaymentActions.markPaymentAsCharged(couponValidation);
                            break;
                        }
                        case PAYMENT_STATUS.BILLED: {
                            PaymentActions.markPaymentAsCharged(couponValidation);
                            if (supplier_ref) {
                                ReservationActions.updateSupplierRef(supplier_ref);
                            }
                            break;
                        }
                        case PAYMENT_STATUS.CANCELLED: {
                            // TODO: (not very good)
                            PaymentActions.markPaymentAsFailed('cancelled');
                            break;
                        }
                        case PAYMENT_STATUS.ERROR: {
                            if (reservation_status.includes('payment')) {
                                PaymentActions.markPaymentAsCharged(couponValidation);
                                break;
                            }
                            notifyOncePerPayment(
                                CALLBACK_PAYMENT_DECLINED,
                                paymentId,
                                classifyPaymentError(error_status_code),
                            );
                            if (allow_retry) {
                                let message = i18n.t('AnIssueOccurredWithPaymentCard');
                                if (
                                    error_status_code ===
                                    PAYMENT_CREDIT_CARD_ERROR_CODES.CREDIT_CARD_NOT_SUPPORTED_LOYALTY_CARD
                                ) {
                                    message = i18n.t('PaymentIsEnabledOnlyWithBanHapoalim');
                                }
                                PaymentActions.setPaymentErrorMessage(message);
                                ReservationActions.restartPaymentWithError();
                                break;
                            }
                            // TODO: (not very good)
                            console.log('We got error status code: ', error_status_code);
                            if (
                                error_status_code ===
                                PAYMENT_CREDIT_CARD_ERROR_CODES.CREDIT_CARD_NOT_SUPPORTED_LOYALTY_CARD
                            ) {
                                console.log('send a message of wrong loyalty card');
                                PaymentActions.setLoyaltyCardError();
                                ReservationActions.restartPaymentWithError();
                            } else {
                                PaymentActions.markPaymentAsFailed('error');
                            }
                            break;
                        }
                        default: {
                            // Do nothing
                            console.warn('Poller: unknown payment type', res.paymentStatus);
                            break;
                        }
                    }

					_self.stopPaymentStatusPolling();
				})
				.fail(function (err) {
					// TODO: if not what we're looking for - setTimeout(doPoll, 5000);
					console.error('doPoll - fail : ', err);
				})
				.always(function (data) {
					// TODO: if not what we're looking for - setTimeout(doPoll, 5000);
					// console.log("doPoll - always : ", data);
					// pollTimoeout = setTimeout(doPoll, 1500);
				});
		})();
	},

	stopPaymentStatusPolling: function () {
		console.log('poll - stop polling');
		status = 'stopped';
		poller = null;
		couponValidation = null;
		paymentCallbacks = {};
	},
};